import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import BeneficiarySelectField from './BeneficiarySelectField';
import { BENEFICIARY_TYPE_OPTIONS } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import type { Beneficiary, SelectOption } from '../../../../types/LegalCase';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

// Só escritórios no select de responsável (o campo é "quem cuida do caso"). Filtro de UX.
const LAW_FIRM_TYPE_OPTIONS: SelectOption[] = BENEFICIARY_TYPE_OPTIONS.filter(
    (o) => o.value === 'LAW_FIRM',
);

const UF_OPTIONS: SelectOption[] = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

// Garante que o valor atual apareça no select mesmo se estiver fora da lista oficial — casos
// antigos (ou vindos da carga da planilha) têm entidade credora em texto livre que não pode
// sumir do form. Injeta a opção corrente, marcada como fora do padrão. Fora do componente.
const withCurrentValue = (options: SelectOption[], current: string): SelectOption[] => {
    if (!current || options.some((o) => o.value === current)) return options;
    return [...options, { value: current, label: `${current} (fora do padrão)` }];
};

export interface LegalCaseFormProps {
    isCreating: boolean;
    errorFields: ErrorField[];
    caseType: string; setCaseType: (v: string) => void;
    pole: string; setPole: (v: string) => void;
    status: string; setStatus: (v: string) => void;
    creditorEntity: string; setCreditorEntity: (v: string) => void;
    // Encerramento (Task 20). Exigidos quando a situação vira "Encerrado".
    closureReason: string; setClosureReason: (v: string) => void;
    closedAt: string; setClosedAt: (v: string) => void;
    closureNotes: string; setClosureNotes: (v: string) => void;
    openedAt: string; setOpenedAt: (v: string) => void;
    externalCaseNumber: string; setExternalCaseNumber: (v: string) => void;
    court: string; setCourt: (v: string) => void;
    district: string; setDistrict: (v: string) => void;
    courtDivision: string; setCourtDivision: (v: string) => void;
    state: string; setState: (v: string) => void;
    courtLink: string; setCourtLink: (v: string) => void;
    consultationPassword: string; setConsultationPassword: (v: string) => void;
    claimValue: number; setClaimValue: (v: number) => void;
    responsibleFirmId: string; setResponsibleFirmId: (v: string) => void;
    // Risco CPC 25 (Task 12). O setter da classificação sugere o percentual no hook.
    cpc25Classification: string; setCpc25Classification: (v: string) => void;
    estimatedLossPercent: number; setEstimatedLossPercent: (v: number) => void;
    provisionOverrideAmount: number; setProvisionOverrideAmount: (v: number) => void;
    provisionOverrideReason: string; setProvisionOverrideReason: (v: string) => void;
    // Encargos (Task 17). Taxas por caso — a taxa é litigada, não constante global.
    interestRateMonthly: number; setInterestRateMonthly: (v: number) => void;
    penaltyRate: number; setPenaltyRate: (v: number) => void;
    description: string; setDescription: (v: string) => void;
    caseTypeOptions: SelectOption[];
    poleOptions: SelectOption[];
    statusOptions: SelectOption[];
    closureReasonOptions: SelectOption[];
    riskClassificationOptions: SelectOption[];
    creditorEntityOptions: SelectOption[];
    // Escritórios cadastrados (já buscados pelo hook de create/edit, filtrados por LAW_FIRM).
    // Evita o form buscar a lista de novo (o Copilot pegou essa chamada redundante).
    lawFirms: Beneficiary[];
    handleCreate: () => void;
}

const LegalCaseForm = (props: LegalCaseFormProps) => {
    const {
        errorFields,
        caseType, setCaseType,
        pole, setPole,
        status, setStatus,
        creditorEntity, setCreditorEntity,
        closureReason, setClosureReason,
        closedAt, setClosedAt,
        closureNotes, setClosureNotes,
        openedAt, setOpenedAt,
        externalCaseNumber, setExternalCaseNumber,
        court, setCourt,
        district, setDistrict,
        courtDivision, setCourtDivision,
        state, setState,
        courtLink, setCourtLink,
        consultationPassword, setConsultationPassword,
        claimValue, setClaimValue,
        responsibleFirmId, setResponsibleFirmId,
        cpc25Classification, setCpc25Classification,
        estimatedLossPercent, setEstimatedLossPercent,
        provisionOverrideAmount, setProvisionOverrideAmount,
        provisionOverrideReason, setProvisionOverrideReason,
        interestRateMonthly, setInterestRateMonthly,
        penaltyRate, setPenaltyRate,
        description, setDescription,
        caseTypeOptions, poleOptions, statusOptions, closureReasonOptions,
        riskClassificationOptions,
        creditorEntityOptions,
        lawFirms,
    } = props;

    const isClosed = status === 'CLOSED';

    return (
        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <SelectTextField
                    label="Tipo do caso"
                    items={caseTypeOptions}
                    value={caseType}
                    onChange={(e) => setCaseType(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'caseType')}
                />
                <SelectTextField
                    label="Polo"
                    items={poleOptions}
                    value={pole}
                    onChange={(e) => setPole(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'pole')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <SelectTextField
                    label="Entidade credora"
                    items={withCurrentValue(creditorEntityOptions, creditorEntity)}
                    value={creditorEntity}
                    onChange={(e) => setCreditorEntity(e.target.value)}
                    emptyLabel="Selecione a entidade..."
                    errorMessage={getErrorMessage(errorFields, 'creditorEntity')}
                />
                <SelectTextField
                    label="Situação"
                    items={statusOptions}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'status')}
                />
            </div>

            {/* Encerramento (Task 20). Aparece só quando a situação é "Encerrado": motivo e data
                são obrigatórios (o backend rejeita com 422 encerrar sem eles), a observação é
                livre. Reabrir (voltar a "Em curso") faz o backend limpar os três. */}
            {isClosed && (
                <div className="border-t border-gray-200 dark:border-slate-700 pt-5 space-y-5">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Encerramento
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                        <SelectTextField
                            label="Motivo do encerramento"
                            items={closureReasonOptions}
                            value={closureReason}
                            onChange={(e) => setClosureReason(e.target.value)}
                            emptyLabel="Selecione o motivo..."
                            errorMessage={getErrorMessage(errorFields, 'closureReason')}
                        />
                        <DateField
                            label="Data do encerramento"
                            value={closedAt}
                            onChange={setClosedAt}
                            errorMessage={getErrorMessage(errorFields, 'closedAt')}
                        />
                    </div>
                    <TextEdit
                        label="Observação do encerramento (opcional)"
                        value={closureNotes}
                        onChange={(e: any) => setClosureNotes(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'closureNotes')}
                    />
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                        A data do encerramento também encerra a contagem de juros do caso.
                    </p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Número do processo (CNJ)"
                    value={externalCaseNumber}
                    onChange={(e: any) => setExternalCaseNumber(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'externalCaseNumber')}
                />
                <DateField
                    label="Data de distribuição"
                    value={openedAt}
                    onChange={setOpenedAt}
                    errorMessage={getErrorMessage(errorFields, 'openedAt')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <CurrencyInput
                    label="Valor da Causa"
                    prefix="R$"
                    fractionDigits={2}
                    value={claimValue}
                    onChange={setClaimValue}
                    errorMessage={getErrorMessage(errorFields, 'claimValue')}
                />
                <BeneficiarySelectField
                    label="Escritório responsável (opcional)"
                    value={responsibleFirmId}
                    onChange={setResponsibleFirmId}
                    beneficiaries={lawFirms}
                    beneficiaryTypeOptions={LAW_FIRM_TYPE_OPTIONS}
                    isSaving={false}
                    errorFields={errorFields}
                    errorFieldName="responsibleFirmId"
                    allowCreate={false}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Foro / Tribunal"
                    value={court}
                    onChange={(e: any) => setCourt(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'court')}
                />
                <TextEdit
                    label="Comarca"
                    value={district}
                    onChange={(e: any) => setDistrict(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'district')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Vara"
                    value={courtDivision}
                    onChange={(e: any) => setCourtDivision(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'courtDivision')}
                />
                <SelectTextField
                    label="UF"
                    items={UF_OPTIONS}
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'state')}
                />
            </div>

            <TextEdit
                label="Link do tribunal"
                value={courtLink}
                onChange={(e: any) => setCourtLink(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'courtLink')}
            />

            <TextEdit
                label="Senha de consulta processual"
                type="password"
                value={consultationPassword}
                onChange={(e: any) => setConsultationPassword(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'consultationPassword')}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2">
                Fica visível só no detalhe do caso, nunca na listagem.
            </p>

            {/* Risco CPC 25 (Task 12). Classificação e percentual são opcionais. Ao escolher a
                classificação, o percentual é pré-preenchido com a sugestão (editável). A
                provisão é calculada pela regra (só passivo provável) no backend; o override é
                o ajuste manual, opcional, com justificativa. */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-5 space-y-5">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Risco CPC 25
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <SelectTextField
                        label="Classificação (opcional)"
                        items={riskClassificationOptions}
                        value={cpc25Classification}
                        onChange={(e) => setCpc25Classification(e.target.value)}
                        emptyLabel="Não avaliado"
                        errorMessage={getErrorMessage(errorFields, 'cpc25Classification')}
                    />
                    <CurrencyInput
                        label="Perda estimada (%)"
                        prefix="%"
                        fractionDigits={2}
                        maxValue={100}
                        value={estimatedLossPercent}
                        onChange={setEstimatedLossPercent}
                        errorMessage={getErrorMessage(errorFields, 'estimatedLossPercent')}
                    />
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    Provisão só é reconhecida para caso passivo classificado como provável (CPC 25).
                    No polo ativo o percentual representa a expectativa de não recuperar o crédito.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <CurrencyInput
                        label="Provisão (ajuste manual, opcional)"
                        prefix="R$"
                        fractionDigits={2}
                        value={provisionOverrideAmount}
                        onChange={setProvisionOverrideAmount}
                        errorMessage={getErrorMessage(errorFields, 'provisionOverrideAmount')}
                    />
                    <TextEdit
                        label="Justificativa do ajuste"
                        value={provisionOverrideReason}
                        onChange={(e: any) => setProvisionOverrideReason(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'provisionOverrideReason')}
                    />
                </div>
            </div>

            {/* Encargos (Task 17 / ADR 004). As taxas ficam **no caso**, não numa constante
                global, porque são litigadas: a planilha do jurídico registra multa de 5% reduzida
                a 2% pelo CDC. Num caso novo vêm pré-preenchidas com a sugestão (1% a.m. / 2%) e
                são editáveis; zerar a taxa desliga o encargo. Juros, multa e valor atualizado em
                reais são calculados na leitura e aparecem na seção Pretensão — aqui só as taxas. */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-5 space-y-5">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Encargos
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <CurrencyInput
                        label="Juros de mora (% ao mês)"
                        prefix="%"
                        fractionDigits={2}
                        maxValue={100}
                        value={interestRateMonthly}
                        onChange={setInterestRateMonthly}
                        errorMessage={getErrorMessage(errorFields, 'interestRateMonthly')}
                    />
                    <CurrencyInput
                        label="Multa (%)"
                        prefix="%"
                        fractionDigits={2}
                        maxValue={100}
                        value={penaltyRate}
                        onChange={setPenaltyRate}
                        errorMessage={getErrorMessage(errorFields, 'penaltyRate')}
                    />
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    Os juros correm da data de entrada do caso no jurídico, pro rata die em mês de
                    30 dias (1% a.m. é o juro legal do art. 406 do CC). A multa incide sobre o
                    principal. Deixe em zero para não calcular encargo.
                </p>
            </div>

            <TextEdit
                label="Observações"
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />
        </div>
    );
};

export default LegalCaseForm;
